// src/companies/bulkCheck.js — „Проверка на контрагентите" (ПРОМТ-36): масовата
// проверка на ДДС номерата на всички контакти от всички фирми във ВИЕС, а на
// нерегистрираните по ДДС — в Търговския регистър. Само админ, в „Мои фирми".
//
// Проверката върви сама на сървъра (services/bulkCheck.js) — тук е само
// таблото: колко са, докъде е стигнала, копчетата Старт/Пауза/Продължи и
// прегледът. Грешките в контактите се поправят сами, щом номерът е проверен
// (services/bulkCheck.js → fixPlan), а „Върни" ги връща.

import { el, clear, field } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { openModal, confirmDialog } from '../shared/modal.js';
import { statCard } from '../shared/ui.js';
import { isoToBg } from '../shared/dates.js';

const n = (x) => Number(x || 0).toLocaleString('bg-BG');
const FILTERS = [
  ['fixed', 'Поправени автоматично'],
  ['review', 'За преглед от човек (името е съвсем различно)'],
  ['invalid', 'Невалиден ДДС номер'],
  ['name', 'Името във ВИЕС е различно'],
  ['fill', 'Празно в CRM-а — ВИЕС/регистърът има'],
  ['unavailable', 'Временно недостъпни'],
  ['all', 'Всички проверени'],
];
const KIND = { fill: 'попълнено празно', name: 'името', vat: 'махнат невалиден ДДС №' };
const STOP_ASK = ['Да спра ли проверката? Проверените номера остават.', 'Спри проверката'];
const STATE = {
  idle: 'Проверката още не е пускана.',
  running: 'Проверката върви — сама, по един номер на няколко секунди. Можете да затворите прозореца.',
  paused: 'Проверката е на пауза. „Продължи" я пуска от същото място.',
  trial_done: 'Пробата приключи. Прегледайте резултата долу; ако изглежда наред — „Продължи с всички".',
  stopped: 'Проверката е спряна. „Старт" я пуска отново — вече проверените номера не се питат пак.',
  done: 'Всички номера са проверени.',
};

// Часът в разбираем вид: „днес 08:00", „утре 08:00" или датата.
function whenText(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const day = (x) => x.getFullYear() + '-' + String(x.getMonth() + 1).padStart(2, '0') + '-' + String(x.getDate()).padStart(2, '0');
  const hm = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 24 * 3600 * 1000);
  if (day(d) === day(today)) return 'днес ' + hm;
  if (day(d) === day(tomorrow)) return 'утре ' + hm;
  return isoToBg(day(d)) + ' ' + hm;
}

export function registryText(r) {
  if (!r) return '';
  if (!r.enabled) return 'Регистърът не е включен (няма ключ за TheCompanyBook) — проверява се само във ВИЕС.';
  const base = 'Регистърът: по ' + (r.daily - r.reserve) + ' фирми на ден (лимитът е ' + r.daily + ' справки за 24 часа, ' + r.reserve + ' остават за бутона при контакта). Днес: ' + n(r.used_24h) + '.';
  const next = r.next_at ? ' Следващата проверка в регистъра — ' + whenText(r.next_at) + (r.candidates ? ' (чакат ' + n(r.candidates) + ' фирми без ДДС регистрация).' : '.') : '';
  return base + next;
}

export function bulkCheckDialog() {
  const body = el('div', { class: 'bulk-check' });
  const lead = el('p', { class: 'imp-lead', text: 'Проверява ДДС номерата на контактите от всички фирми в европейския регистър ВИЕС. Една фирма, която е клиент на няколко наши фирми, се проверява веднъж. Пример: „BG 123 456 789", „bg123456789" и „123456789" са един и същ номер. Контакт без ДДС номер, но с ЕИК, се пробва като BG + ЕИК — така се вижда дали е регистриран по ДДС.' });
  const note = el('p', { class: 'muted', text: 'Щом номерът е проверен, грешките в контакта се поправят сами: празното име, адрес и град се попълват от ВИЕС/регистъра; сбърканото име на българска фирма става като в регистъра (напр. „Газ Ойл Терминла" → „ГАЗ ОЙЛ ТЕРМИНАЛ ЕООД"); невалиден ДДС номер се маха от контакта (старият се пази и се вижда). Ако името е съвсем различно — по-скоро е сбъркан номерът, затова не се пипа, а е в „За преглед от човек". Всяка поправка се връща с „Върни". Издадените документи не се пипат.' });
  const autoBox = el('input', { type: 'checkbox', id: 'bc-auto', onChange: () => act('auto-fix', { on: autoBox.checked }) });
  const autoLine = el('label', { for: 'bc-auto', style: 'display:flex;gap:6px;align-items:center;margin:4px 0 8px' }, autoBox, el('span', { text: 'Поправяй грешките автоматично, щом номерът е проверен' }));
  const cards = el('div', { class: 'cards' });
  const stateLine = el('p', { 'aria-live': 'polite' });
  const regLine = el('p', { class: 'muted' });
  const buttons = el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap;margin:8px 0 16px' });
  const filter = el('select', {}, ...FILTERS.map(([v, l]) => el('option', { value: v, text: l })));
  const out = el('div', { 'aria-live': 'polite' });
  body.append(lead, note, autoLine, cards, stateLine, regLine, buttons, field('Покажи', filter, { size: 'm' }), out);
  let timer = null;

  // ask: [въпрос, надпис на бутона] — „Да/Не" без ясен бутон не се пита.
  async function act(path, payload, ask) {
    if (ask && !(await confirmDialog(ask[0], { okText: ask[1] }))) return;
    try {
      const r = await api('/bulk-check/' + path, { method: 'POST', body: JSON.stringify(payload || {}) });
      // Попълването казва колко контакта са пипнати — иначе човек не знае дали е станало.
      if (path === 'fix') toast('Поправени: ' + n(r.fixed) + ' контакта (попълнени: ' + n(r.kinds.fill) + ', име: ' + n(r.kinds.name) + ', махнат невалиден ДДС №: ' + n(r.kinds.vat) + ').');
      if (path === 'undo') toast('Върнати: ' + n(r.undone) + '.');
      if (path === 'fill') toast('Попълнени: ' + n(r.filled) + (r.skipped ? ' · пропуснати (вече попълнени или сменени): ' + n(r.skipped) : '') + '.');
      if (r && r.status) drawStatus(r.status); else refresh();
      loadRows();
    } catch (e) { toast(e.message, true); }
  }

  function btn(text, onClick, ghost) {
    return el('button', { class: 'btn' + (ghost ? ' ghost' : ''), type: 'button', text, onClick });
  }

  function drawStatus(st) {
    if (!st) return;
    const c = st.counts || {};
    clear(cards);
    cards.append(
      statCard('Контакти', n(c.contacts), c.without_key ? n(c.without_key) + ' без ДДС № и ЕИК (или физ. лица)' : ''),
      statCard('Уникални номера', n(c.keys), 'всеки се пита веднъж'),
      statCard('Проверени', n(c.checked), 'остават ' + n(c.remaining)),
      statCard('Валидни', n(c.valid)),
      statCard('Невалидни ДДС №', n(c.invalid), c.not_vat ? n(c.not_vat) + ' с ЕИК не са регистрирани по ДДС' : '', { warn: c.invalid > 0 }),
      statCard('Поправени автоматично', n(c.fixed), c.fixable ? 'чакат поправка: ' + n(c.fixable) : ''),
      statCard('Грешки', n(c.unavailable), 'временно недостъпни — „Старт" ги пробва пак', { warn: c.unavailable > 0 }));
    let s = STATE[st.state] || '';
    if (st.state === 'running' && st.trial) s += ' Това е пробата: първите ' + st.trial_limit + ' номера (проверени ' + n(st.checked_in_run) + ').';
    if (st.wait_until) s += ' ВИЕС е претоварен — чакаме до ' + whenText(st.wait_until) + ' и продължаваме сами.';
    const cw = Object.keys(st.country_wait || {});
    if (cw.length) s += ' В момента не отговаря: ' + cw.join(', ') + ' — минаваме на другите държави.';
    if (st.worker === false) s = 'Фоновите задачи на сървъра са изключени (настройката JOBS_MODE=inline в Coolify липсва) — проверката няма да тръгне, докато не се включат. ' + s;
    stateLine.textContent = s;
    regLine.textContent = registryText(st.registry);
    autoBox.checked = st.auto_fix !== false;
    fixable = c.fixable || 0;
    clear(buttons);
    if (['idle', 'stopped', 'done'].includes(st.state)) {
      buttons.appendChild(btn(st.trial_done ? 'Старт' : 'Старт (проба: първите ' + st.trial_limit + ')', () => act('start')));
    }
    if (st.state === 'running') buttons.append(btn('Пауза', () => act('pause'), true), btn('Спри', () => act('stop', null, STOP_ASK), true));
    if (st.state === 'paused') buttons.append(btn('Продължи', () => act('resume')), btn('Спри', () => act('stop', null, STOP_ASK), true));
    if (st.state === 'trial_done') buttons.append(btn('Продължи с всички', () => act('resume', { all: true })), btn('Спри', () => act('stop', null, STOP_ASK), true));
    clearTimeout(timer);
    if (st.state === 'running') timer = setTimeout(refresh, 10000);
  }

  // Отметките живеят извън таблицата: опресняването на всеки 10 секунди
  // пречертава редовете и преди изтриваше избора (Макс, 30.10.2026).
  const picked = new Set();
  let fixable = 0;
  async function refresh() {
    if (!body.isConnected) return; // прозорецът е затворен
    try { drawStatus(await api('/bulk-check/status')); loadRows(); } catch (e) { stateLine.textContent = e.message; }
  }

  async function loadRows() {
    let r;
    try { r = await api('/bulk-check/results?filter=' + filter.value + '&limit=500'); }
    catch (e) { clear(out); out.appendChild(el('p', { class: 'sev-warn', role: 'alert', text: e.message })); return; }
    clear(out);
    if (!r.total) { out.appendChild(el('p', { class: 'muted', text: 'Няма редове за този избор.' })); return; }
    const fillBtn = btn('Попълни отметнатите' + (picked.size ? ' (' + n(picked.size) + ')' : ''), () => {
      if (!picked.size) { toast('Изберете поне един контакт.', true); return; }
      const ids = [...picked];
      picked.clear();
      act('fill', { contact_ids: ids }, ['Да попълня ли празните полета (име, адрес, град) на ' + n(picked.size) + ' контакта от ВИЕС/регистъра? Попълнените полета не се сменят.', 'Попълни']);
    });
    const fillable = r.rows.filter((x) => x.fill.length);
    // Колко са ВСИЧКИ за попълване (не само показаните 500) — бутонът за всички.
    let fillTotal = 0;
    try { fillTotal = filter.value === 'fill' ? r.total : (await api('/bulk-check/results?filter=fill&limit=1')).total; } catch (e) { fillTotal = 0; }
    if (!out.isConnected) return;
    const fillAllBtn = fillTotal ? btn('Попълни празните полета на всички (' + n(fillTotal) + ')', () =>
      act('fill', { all: true }, ['Да попълня ли празните полета (име, адрес, град) на всички ' + n(fillTotal) + ' контакта от ВИЕС/регистъра? Попълва се само празното — нищо попълнено не се сменя. Проверката може да продължава; новите след нея — пак с този бутон.', 'Попълни'])) : null;
    const all = el('input', { type: 'checkbox', 'aria-label': 'Избери всички', onChange: () => {
      out.querySelectorAll('input[data-cid]').forEach((cb) => { cb.checked = all.checked; cb.dispatchEvent(new Event('change')); });
    } });
    for (const x of r.rows) { x.fixed = x.fixed || []; x.will_fix = x.will_fix || []; x.fixed_before = x.fixed_before || {}; }
    const tb = el('tbody', {}, ...r.rows.map((x) => {
      const pickable = filter.value === 'fixed' ? x.fixed.length > 0 : x.fill.length > 0;
      const cb = pickable ? el('input', { type: 'checkbox', checked: picked.has(x.contact_id), 'data-cid': String(x.contact_id), 'aria-label': 'Избери ' + x.name, onChange: () => { if (cb.checked) picked.add(x.contact_id); else picked.delete(x.contact_id); } }) : null;
      const tag = x.status === 'valid' ? 'green' : (x.status === 'invalid' && x.source === 'vat' ? 'red' : '');
      return el('tr', {},
        el('td', {}, cb),
        el('td', { text: x.company }),
        el('td', {}, el('div', { text: x.name }), x.address || x.city ? el('div', { class: 'muted', text: [x.address, x.city].filter(Boolean).join(', ') }) : null,
          x.fixed.length ? el('div', { class: 'tag green', text: 'поправено: ' + x.fixed.map((k) => KIND[k]).join(', ') + (x.fixed_before.name ? ' (беше „' + x.fixed_before.name + '")' : '') + (x.vat_removed ? ' (беше ' + x.vat_removed + ')' : '') }) : null,
          !x.fixed.length && x.will_fix.length ? el('div', { class: 'muted', text: 'ще се поправи: ' + x.will_fix.map((k) => KIND[k]).join(', ') }) : null),
        el('td', { text: x.key }),
        el('td', {}, el('span', { class: 'tag ' + tag, text: x.status_text }), x.error ? el('div', { class: 'muted', text: x.error }) : null),
        el('td', {}, el('div', { text: x.public_name || '—' }), x.name_differs ? el('div', { class: 'sev-warn', text: 'различно име' }) : null),
        el('td', {}, x.public_address || '—', x.fill.length ? el('div', { class: 'muted', text: 'ще се попълни: ' + x.fill.map((f) => ({ name: 'име', address: 'адрес', city: 'град' })[f]).join(', ') }) : null));
    }));
    const fixAllBtn = fixable ? btn('Поправи всичко сега (' + n(fixable) + ')', () =>
      act('fix', {}, ['Да поправя ли ' + n(fixable) + ' контакта: празното от ВИЕС/регистъра, сбърканите имена на български фирми и махане на невалидните ДДС номера? Всичко се връща с „Върни".', 'Поправи'])) : null;
    // Бутонът за поправка е винаги отгоре, когато има какво — и в „Поправени
    // автоматично" (там се отваря екранът и Макс не го виждаше, 30.10.2026).
    if (fixAllBtn) out.appendChild(el('div', { style: 'margin:6px 0' }, fixAllBtn,
      el('span', { class: 'muted', style: 'margin-left:8px', text: 'Чакат поправка — проверени, преди автоматиката да е включена. В края на проверката се оправят и сами.' })));
    if (filter.value === 'fixed') {
      out.appendChild(el('div', { style: 'margin:6px 0;display:flex;gap:8px;flex-wrap:wrap;align-items:center' },
        btn('Върни отметнатите', () => {
          if (!picked.size) { toast('Изберете поне един контакт.', true); return; }
          const ids = [...picked];
          picked.clear();
          act('undo', { contact_ids: ids }, ['Да върна ли ' + n(ids.length) + ' контакта, както бяха преди поправката? Сменено после от човек не се пипа.', 'Върни']);
        }, true),
        btn('Върни всички', () => act('undo', { all: true }, ['Да върна ли ВСИЧКИ автоматично поправени контакти, както бяха? Сменено после от човек не се пипа.', 'Върни всички']), true)));
    }
    if (filter.value !== 'fixed' && (fillable.length || fillAllBtn)) out.appendChild(el('div', { style: 'margin:6px 0;display:flex;gap:8px;flex-wrap:wrap;align-items:center' }, fillAllBtn, fillable.length ? fillBtn : null,
      el('span', { class: 'muted', text: 'Попълва само празните полета — попълненото не се сменя.' })));
    out.appendChild(el('p', { class: 'muted', text: 'Показани ' + n(r.rows.length) + ' от ' + n(r.total) + '.' }));
    out.appendChild(el('div', { class: 'table-wrap', style: 'max-height:420px;overflow:auto' }, el('table', {},
      el('thead', {}, el('tr', {},
        el('th', { scope: 'col' }, (filter.value === 'fixed' ? r.rows.some((x) => x.fixed.length) : fillable.length) ? all : null),
        el('th', { scope: 'col', text: 'Фирма' }), el('th', { scope: 'col', text: 'Контакт в CRM-а' }),
        el('th', { scope: 'col', text: 'Номер' }), el('th', { scope: 'col', text: 'Резултат' }),
        el('th', { scope: 'col', text: 'Име (ВИЕС/регистър)' }), el('th', { scope: 'col', text: 'Адрес (ВИЕС/регистър)' }))), tb)));
  }

  filter.addEventListener('change', loadRows);
  // Филтърът и отметките не са „незаписана работа" — затварянето не пита.
  openModal({ title: 'Проверка на контрагентите', body, wide: true, dirtyCheck: () => false, onDismiss: () => clearTimeout(timer) });
  refresh();
}
